/**
 * Service d'authentification — comptes, sessions, mots de passe.
 *
 * Toute la logique parle à la micro-API PHP (`/auth/*`). Le rôle n'est jamais
 * envoyé par le client : il est lu dans le profil renvoyé par la base. Ce qui
 * a disparu avec l'ancien fournisseur d'identité et n'existe plus côté
 * navigateur : le stockage d'un mot de passe, le choix d'un rôle à
 * l'inscription, la moindre requête SQL.
 *
 * L'état de session est exposé sous forme de petit magasin observable, pour que
 * `useAuth` reste un simple abonnement.
 */
import {
  request,
  rpc,
  storeTokens,
  setSession,
  getSession,
  onSessionChange,
  type SessionUser,
} from '@/lib/http';

export interface UserProfile {
  id: string;
  email: string;
  role: 'driver' | 'fleet_manager' | 'ceo_admin';
  full_name: string;
  phone: string | null;
  company_id: string | null;
  company_name: string | null;
  company_country: string | null;
  hr_email: string | null;
  locale: string;
  work_start: string | null;
  work_end: string | null;
  work_days: number[];
  /** Barème kilométrique — voir mysql/27. Choisissable seulement sans société. */
  rate_mode: 'auto' | 'fixed' | 'custom';
  rate_id: string | null;
  custom_rate_per_km: number | null;
  geo_consent_at: string | null;
  created_at: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: 'Bearer';
  user: SessionUser;
}

export interface AuthState {
  loading: boolean;
  isSignedIn: boolean;
  user: SessionUser | null;
  profile: UserProfile | null;
}

// ── Magasin observable ──────────────────────────────────────────────────────

let state: AuthState = {
  loading: true,
  isSignedIn: !!getSession(),
  user: getSession()?.user ?? null,
  profile: null,
};

const subscribers = new Set<() => void>();

function set(patch: Partial<AuthState>) {
  state = { ...state, ...patch };
  subscribers.forEach((fn) => fn());
}

export const authStore = {
  getState: () => state,
  subscribe(fn: () => void) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  },
};

// Une session invalidée ailleurs (rafraîchissement refusé, autre onglet) doit
// vider l'état ici aussi.
onSessionChange((s) => {
  if (!s) set({ isSignedIn: false, user: null, profile: null, loading: false });
  else set({ isSignedIn: true, user: s.user });
});

// ── Actions ─────────────────────────────────────────────────────────────────

/** Crée la ligne `profiles` si besoin, puis la relit. Idempotent. */
async function loadProfile(): Promise<UserProfile | null> {
  await rpc('profile.ensure');
  return rpc<UserProfile>('profile.get');
}

/**
 * Garde de démarrage.
 *
 * `useAuth` appelle bootstrap au montage, et le hook est monté par plusieurs
 * composants (contexte de rôle, écrans). Sans ce verrou, chaque nouveau montage
 * repassait `loading` à true : l'app affichait son écran de chargement, ce qui
 * démontait l'écran qui venait de monter, qui rappelait bootstrap au montage
 * suivant — une boucle de rendu visible comme un loader clignotant.
 */
let booted = false;

export const authService = {
  /** À appeler une fois au démarrage : reprend la session stockée. */
  async bootstrap(): Promise<void> {
    if (booted) return;
    booted = true;
    if (!getSession()) {
      set({ loading: false, isSignedIn: false, user: null, profile: null });
      return;
    }
    set({ loading: true });
    try {
      set({ profile: await loadProfile(), isSignedIn: true, user: getSession()?.user ?? null });
    } catch {
      // Jeton périmé ou révoqué : on repart d'un état propre.
      setSession(null);
      set({ profile: null, isSignedIn: false, user: null });
    } finally {
      set({ loading: false });
    }
  },

  async register(email: string, password: string, fullName: string): Promise<UserProfile | null> {
    const res = await request<TokenResponse>(
      '/auth/register',
      { email, password, full_name: fullName },
      { auth: false },
    );
    storeTokens(res);
    booted = true;
    const profile = await loadProfile();
    set({ isSignedIn: true, user: res.user, profile, loading: false });
    return profile;
  },

  async login(email: string, password: string): Promise<UserProfile | null> {
    const res = await request<TokenResponse>('/auth/login', { email, password }, { auth: false });
    storeTokens(res);
    booted = true;   // le profil est lu juste après : pas de second démarrage
    const profile = await loadProfile();
    set({ isSignedIn: true, user: res.user, profile, loading: false });
    return profile;
  },

  async signOut(everywhere = false): Promise<void> {
    // Une déconnexion autorise un nouveau démarrage complet à la prochaine
    // connexion (profil à relire, session à reprendre).
    booted = false;
    const s = getSession();
    try {
      if (s) await request('/auth/logout', { refresh_token: s.refresh_token, everywhere });
    } catch {
      /* la session locale est effacée quoi qu'il arrive */
    }
    setSession(null);
    set({ isSignedIn: false, user: null, profile: null, loading: false });
  },

  async refreshProfile(): Promise<UserProfile | null> {
    if (!getSession()) return null;
    const profile = await loadProfile();
    set({ profile });
    return profile;
  },

  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('/auth/password/change', { current, next }),

  /** Réponse identique qu'un compte existe ou non : pas de fuite d'annuaire. */
  forgotPassword: (email: string) =>
    request<{ sent: true }>('/auth/password/forgot', { email }, { auth: false }),

  resetPassword: (token: string, password: string) =>
    request<{ ok: true }>('/auth/password/reset', { token, password }, { auth: false }),

  /** Seule voie vers le rôle fleet_manager : créer sa société côté serveur. */
  async claimCompany(name: string, vat?: string, country = 'BE'): Promise<UserProfile | null> {
    await rpc('company.claim', { name, vat, country });
    return authService.refreshProfile();
  },

  /** RGPD — export puis effacement complet du compte. */
  exportData: () => rpc<{ data: unknown }>('gdpr.export'),
  eraseAccount: async () => {
    const id = getSession()?.user.id;
    if (!id) return;
    await rpc('gdpr.erase', { user_id: id });
    setSession(null);
    set({ isSignedIn: false, user: null, profile: null, loading: false });
  },
};
